import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { getVisualStyleDisplayName, getVoiceDisplayName, STORY_LANGUAGES } from '../settings/staticData';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { FASTAPI_HOST } from '../settings/config';
import Cookies from "js-cookie";
import axios from "axios";
import { showStyledConfirm } from '../components/StyledAlert';


function CreationSettingsPage({
  name,
  setName,
  gender,
  setGender,
  visualStyle,
  setVisualStyle,
  age,
  setAge,
  interest,
  setInterest,
  voice,
  setVoice,
  cognitiveLevel,
  setCognitiveLevel,
  setConversationLevel,
  pages,
  setPages,
  imageStyle,
  setImageStyle,
  getImageStylesInLanguage,
  cognitiveLevelsData,
  currentLang,
  t,
  onClose,
  onOpenBuddySettings,
  activitySettings,
  activitySettingsTitle,
  onSaveActivitySettings,
  visualStylesData,
  voicesData,
  suppressSessionExpiredAlert = false,
  // Optional: allow parent components to control story language.
  storyLanguage: storyLanguageProp,
  setStoryLanguage: setStoryLanguageProp,
  roleId,
  setRoleId,
  }) {
    const [localStoryLanguage, setLocalStoryLanguage] = useState('English');
    const storyLanguage = storyLanguageProp ?? localStoryLanguage;
    const setStoryLanguage = setStoryLanguageProp ?? setLocalStoryLanguage;

  const langKey = currentLang === 'English' ? 'en' : currentLang === 'French' ? 'fr' : currentLang === 'Traditional Chinese' ? 'zh-TW' : currentLang === 'Simplified Chinese' ? 'zh-CN' : 'en';
  const [successMsg, setSuccessMsg] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contentLanguageInfoAnchor, setContentLanguageInfoAnchor] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(roleId || '');
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const previousRoleBeforeDraftRef = useRef(null);
  React.useEffect(() => {
    if (!visualStyle && visualStylesData && visualStylesData.length > 0) {
      setVisualStyle(visualStylesData[0].key);
    }
  }, [visualStyle, setVisualStyle, visualStylesData]);
  React.useEffect(() => {
    if (!voice && voicesData && voicesData.length > 0) {
      setVoice(voicesData[0].key);
    }
  }, [voice, setVoice, voicesData]);
  React.useEffect(() => {
    if (!gender) {
      setGender("Male");
    }
  }, [gender, setGender]);
  React.useEffect(() => {
    if (!age) {
      setAge(10);
    }
  }, [age, setAge]);

    const userToken = Cookies.get("user_token");
    const { signOut } = useAuth();

    // Sign out if no user token on mount
    useEffect(() => {
      if (!userToken && !suppressSessionExpiredAlert) {
        setWarning(t?.sb_story_session_expired || "User session expired/lost. Please sign in again.");
        const signOutTimer = setTimeout(() => {
          if (signOut) signOut();
        }, 1500);

        return () => clearTimeout(signOutTimer);
      }

      setWarning("");
    }, [userToken, signOut, suppressSessionExpiredAlert, t]);
  const applyRole = (userRole = {}) => {
    setName(userRole.name || "");
    setGender(userRole.gender || "Male");
    setVisualStyle(userRole.visual_style || visualStylesData?.[0]?.key || "");
    setAge(userRole.age || 10);
    setInterest(userRole.interest || "");
    setVoice(userRole.voice || voicesData?.[0]?.key || "");
    setCognitiveLevel(userRole.written_language_level || userRole.cognitive_level || "Intermediate");
    setConversationLevel(userRole.written_language_level || userRole.cognitive_level || "Intermediate");
    setImageStyle(userRole.image_style || "");
    setStoryLanguage(userRole.story_lang || 'English');
  };

  const loadUserRoles = async (preferredRoleId = null) => {
    if (!userToken) return;
    setLoading(true);
    setError("");
    try {
      const [rolesRes, preferencesRes] = await Promise.all([
        axios.get(`${FASTAPI_HOST}/api/get_user_roles`, {
          headers: { Authorization: `Bearer ${userToken}` }
        }),
        axios.get(`${FASTAPI_HOST}/api/user-preferences`, {
          headers: { Authorization: `Bearer ${userToken}` }
        })
      ]);
      const roles = Array.isArray(rolesRes.data?.user_roles) ? rolesRes.data.user_roles : [];
      const activeId = preferredRoleId ?? rolesRes.data?.active_role_id ?? roles[0]?.id ?? null;
      const activeRole = roles.find((role) => role.id === activeId) || roles[0] || {};
      setPages(Number(preferencesRes.data?.page_count) || 6);
      setUserRoles(roles);
      setSelectedRoleId(activeRole.id || '');
      setIsCreatingRole(false);
      if (typeof setRoleId === 'function') setRoleId(activeRole.id || null);
      applyRole(activeRole);
    } catch (err) {
      setError("Error fetching user roles: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserRoles();
  }, [userToken]);


  const selectRole = async (nextRoleId) => {
    const numericRoleId = Number(nextRoleId);
    const nextRole = userRoles.find((role) => role.id === numericRoleId);
    if (!nextRole || !userToken) return;

    setLoading(true);
    setError("");
    try {
      await axios.put(`${FASTAPI_HOST}/api/user-preferences`, {
        active_role_id: numericRoleId,
      }, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      setSelectedRoleId(numericRoleId);
      setIsCreatingRole(false);
      previousRoleBeforeDraftRef.current = null;
      if (typeof setRoleId === 'function') setRoleId(numericRoleId);
      applyRole(nextRole);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to select role.");
    } finally {
      setLoading(false);
    }
  };

  const startNewRole = () => {
    previousRoleBeforeDraftRef.current = userRoles.find(
      (role) => role.id === Number(selectedRoleId || roleId)
    ) || null;
    setIsCreatingRole(true);
    setSelectedRoleId('');
    applyRole({
      gender: 'Male',
      age: 10,
      cognitive_level: 'Intermediate',
      written_language_level: 'Intermediate',
      conversation_level: 'Intermediate',
      story_lang: 'English',
    });
    setSuccessMsg("");
    setError("");
  };

  useEffect(() => () => {
    if (previousRoleBeforeDraftRef.current) {
      applyRole(previousRoleBeforeDraftRef.current);
    }
  }, []);

  const deleteRole = async (roleIdToDelete) => {
    if (!roleIdToDelete || userRoles.length <= 1 || !userToken) return;
    const selectedRole = userRoles.find((role) => role.id === Number(roleIdToDelete));
    if (!selectedRole) return;
    const confirmed = await showStyledConfirm(
      (t?.role_delete_confirm || 'Delete the profile "{name}"? This cannot be undone.')
        .replace('{name}', selectedRole?.name || ''),
      {
        confirmLabel: t?.role_delete || 'Delete profile',
        cancelLabel: t?.sb_cancel || 'Cancel',
      }
    );
    if (!confirmed) return;

    setLoading(true);
    setError("");
    try {
      const res = await axios.delete(`${FASTAPI_HOST}/api/user-roles/${roleIdToDelete}`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      await loadUserRoles(res.data?.active_role_id ?? null);
      setSuccessMsg(t?.role_deleted || 'Profile deleted.');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete role.');
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {warning && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}
      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}
      <Accordion defaultExpanded sx={{ mt: 0 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t?.settings_personalization || "Profile"}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t?.settings_personalization_help || "Personalize stories and support for each participant."}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="active-role-label">{t?.role_select || 'Active participant profile'}</InputLabel>
              <Select
                id="active-role"
                labelId="active-role-label"
                value={isCreatingRole ? '__new_role__' : selectedRoleId}
                label={t?.role_select || 'Active participant profile'}
                renderValue={(value) => {
                  if (value === '__new_role__') return t?.role_new || 'New profile';
                  return userRoles.find((role) => role.id === Number(value))?.name || '';
                }}
                onChange={(event) => {
                  if (event.target.value === '__add_role__') startNewRole();
                  else selectRole(event.target.value);
                }}
                disabled={loading}
                MenuProps={{ disablePortal: true }}
              >
                {isCreatingRole && (
                  <MenuItem value="__new_role__" disabled>{t?.role_new || 'New profile'}</MenuItem>
                )}
                {userRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography component="span" noWrap sx={{ flex: 1 }}>{role.name}</Typography>
                    {userRoles.length > 1 ? (
                      <Tooltip title={t?.role_delete || 'Delete profile'} placement="left">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={loading}
                          aria-label={`${t?.role_delete || 'Delete profile'}: ${role.name}`}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            deleteRole(role.id);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </MenuItem>
                ))}
                <MenuItem value="__add_role__" sx={{ color: '#586F3C', fontWeight: 700 }}>
                  ＋ {t?.role_add || 'Add profile'}…
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField label={t["sb_name"]} value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 160, flex: 1 }} />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="participant-gender-label">{t["sb_gender"]}</InputLabel>
              <Select id="participant-gender" labelId="participant-gender-label" label={t["sb_gender"]} value={gender} onChange={(e) => setGender(e.target.value)} MenuProps={{ disablePortal: true }}>
                <MenuItem value="Male">{t["sb_male"]}</MenuItem>
                <MenuItem value="Female">{t["sb_female"]}</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100, mr: 2 }}>
              <InputLabel id="participant-age-label">{t["sb_age"]}</InputLabel>
              <Select id="participant-age" labelId="participant-age-label" label={t["sb_age"]} value={age} onChange={e => setAge(Number(e.target.value))} MenuProps={{ disablePortal: true }}>
                {[...Array(12)].map((_, i) => {
                  const val = i + 5;
                  return <MenuItem key={val} value={val}>{val}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: { xs: 120, sm: 160, md: 180 } }}>
              <InputLabel id="visual-style-label">{t["sb_visual_style"]}</InputLabel>
              <Select id="visual-style" labelId="visual-style-label" label={t["sb_visual_style"]} value={visualStyle} onChange={(e) => setVisualStyle(e.target.value)} MenuProps={{ disablePortal: true }}>
                {visualStylesData && visualStylesData.map(v => (
                  <MenuItem key={v.key} value={v.key}>{getVisualStyleDisplayName(v.key, currentLang === 'English' ? 'en' : currentLang === 'French' ? 'fr' : currentLang === 'Traditional Chinese' ? 'zh-TW' : currentLang === 'Simplified Chinese' ? 'zh-CN' : 'en')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Story language belongs to the Role; conversation follows the UI language. */}
            <Box sx={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControl sx={{ flex: 1, minWidth: 120 }}>
                <InputLabel id="story-language-label">{t["story_language"] || "Content language"}</InputLabel>
                <Select
                  id="story-language"
                  labelId="story-language-label"
                  value={storyLanguage}
                  label={t["story_language"] || "Content language"}
                  onChange={e => setStoryLanguage(e.target.value)}
                  MenuProps={{ disablePortal: true }}
                >
                  {STORY_LANGUAGES.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                aria-label={t?.content_language_info || 'About content language'}
                aria-describedby={contentLanguageInfoAnchor ? 'content-language-info' : undefined}
                onClick={(event) => setContentLanguageInfoAnchor(event.currentTarget)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
              <Popover
                id="content-language-info"
                open={Boolean(contentLanguageInfoAnchor)}
                anchorEl={contentLanguageInfoAnchor}
                onClose={() => setContentLanguageInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ zIndex: 2300 }}
              >
                <Typography sx={{ p: 1.5, maxWidth: 280, fontSize: 14 }}>
                  {t?.content_language_note || "For written content and conversations—not menus, buttons, or instructions."}
                </Typography>
              </Popover>
            </Box>
            <FormControl sx={{ flex: 1, minWidth: 120 }}>
              <InputLabel id="participant-voice-label">{t["sb_voice"] || "Voice"}</InputLabel>
              <Select
                id="participant-voice"
                labelId="participant-voice-label"
                label={t["sb_voice"] || "Voice"}
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                MenuProps={{ disablePortal: true }}
              >
                {voicesData && voicesData.map((v) => (
                  <MenuItem key={v.key} value={v.key}>{getVoiceDisplayName(v.key, langKey)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel id="written-language-level-label">{t["sb_cognitive_level"] || "Language level"}</InputLabel>
              <Select
                id="written-language-level"
                labelId="written-language-level-label"
                value={cognitiveLevel || "Intermediate"}
                label={t["sb_cognitive_level"] || "Language level"}
                onChange={(event) => {
                  setCognitiveLevel(event.target.value);
                  setConversationLevel(event.target.value);
                }}
                MenuProps={{ disablePortal: true }}
              >
                {cognitiveLevelsData.map((level) => (
                  <MenuItem key={level.Name} value={level.Name}>
                    {t[`photo_story_level_${level.Name.toLowerCase()}`] || level.Label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1, minWidth: 180 }}>
              <InputLabel id="story-page-count-label">{t["photo_story_page_count"] || "Number of Story Pages"}</InputLabel>
              <Select
                id="story-page-count"
                labelId="story-page-count-label"
                value={Number(pages) || 6}
                label={t["photo_story_page_count"] || "Number of Story Pages"}
                onChange={(event) => setPages(Number(event.target.value))}
                MenuProps={{ disablePortal: true }}
              >
                {[3, 4, 5, 6, 7, 8].map((pageCount) => (
                  <MenuItem key={pageCount} value={pageCount}>{pageCount}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 2, width: '100%', display: 'flex' }}>
            <TextField
              label={t["sb_interest"]}
              value={interest}
              onChange={(e) => setInterest(e.target.value)}
              fullWidth
              sx={{ flex: 1, minWidth: 240, maxWidth: '100%' }}
            />
          </Box>
        </AccordionDetails>
      {/*</Accordion>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>{t["sb_section_story"] || "Story Settings"}</Typography></AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
            <FormControl sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel>{t["sb_voice"] || "Voice"}</InputLabel>
              <Select value={voice} onChange={(e) => setVoice(e.target.value)} MenuProps={{ disablePortal: true }}>
                {getVoicesInLanguage(currentLang).map((v) => (
                  <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
             <FormControl sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel>{t["sb_cognitive_level"]}</InputLabel>
              <Select value={cognitiveLevel} onChange={(e) => setCognitiveLevel(e.target.value)} MenuProps={{ disablePortal: true }}>
                {cognitiveLevelsData.map(c => (
                  <MenuItem key={c.Name} value={c.Name}>{c.Label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}
          {/* TODO: Image Style selection hidden later */}
          {/* <Box mt={2} />
          <FormControl fullWidth>
            <InputLabel>{t["sb_image_style"] || "Image Style"}</InputLabel>
            <Select value={imageStyle} onChange={(e) => setImageStyle(e.target.value)} MenuProps={{ disablePortal: true }}>
              {getImageStylesInLanguage(currentLang).map((v) => (
                <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl> 
          <Box mt={2} /> 
        </AccordionDetails> */}
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t?.settings_conversation || "Conversation"}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t?.settings_conversation_help || "Manage the Buddy used in conversations."}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {typeof onOpenBuddySettings === 'function' && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<SettingsIcon sx={{ fontSize: '24px !important' }} />}
                onClick={onOpenBuddySettings}
                disabled={loading}
                sx={{ flex: 1, minWidth: 220, height: 56 }}
              >
                {t?.buddy_settings_button || 'Buddy settings'}
              </Button>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {activitySettings ? (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{activitySettingsTitle || t?.sb_section_story || 'Story Settings'}</Typography>
          </AccordionSummary>
          <AccordionDetails>{activitySettings}</AccordionDetails>
        </Accordion>
      ) : null}

      <Box sx={{ order: -1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={() => {
              if (previousRoleBeforeDraftRef.current) {
                applyRole(previousRoleBeforeDraftRef.current);
                previousRoleBeforeDraftRef.current = null;
              }
              onClose();
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        ) : <Box />}
        <button
          style={{
            background: '#274D3B',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            padding: '8px 24px',
            fontWeight: 600,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
          }}
          onClick={async () => {
            if (!userToken) {
              setWarning(t?.sb_story_session_expired || "User session expired/lost. Please sign in again.");
              setTimeout(() => {
                if (signOut) signOut();
              }, 1500);
              return;
            }
            const payload = {
              name,
              gender,
              visualStyle,
              age,
              interest,
              voice,
              cognitiveLevel,
              writtenLanguageLevel: cognitiveLevel,
              conversationLevel: cognitiveLevel,
              imageStyle,
              storyLanguage,
              roleId: isCreatingRole ? undefined : (selectedRoleId || roleId),
              createNewRole: isCreatingRole,
            };
            setLoading(true);
            setError("");
            setSuccessMsg("");
            try {
              const preferencesRes = await axios.put(
                `${FASTAPI_HOST}/api/user-preferences`,
                {
                  ui_language: currentLang || "English",
                  page_count: Number(pages) || 6
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userToken}`
                  }
                }
              );
              const roleRes = await axios.post(
                `${FASTAPI_HOST}/api/save_role_settings`,
                payload,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userToken}`
                  }
                }
              );
              setPages(Number(preferencesRes.data?.page_count) || 6);
              const savedRole = roleRes.data?.user_role;
              if (savedRole?.id) {
                setUserRoles((roles) => {
                  const exists = roles.some((role) => role.id === savedRole.id);
                  return exists
                    ? roles.map((role) => role.id === savedRole.id ? savedRole : role)
                    : [savedRole, ...roles];
                });
                setSelectedRoleId(savedRole.id);
                setIsCreatingRole(false);
                previousRoleBeforeDraftRef.current = null;
                if (typeof setRoleId === 'function') setRoleId(savedRole.id);
              }
              if (typeof onSaveActivitySettings === 'function') {
                await onSaveActivitySettings();
              }
              setSuccessMsg('Role settings saved!');
              if (onClose) onClose();
            } catch (e) {
              const msg = 'Error saving role settings: ' + (e.response?.data?.detail || e.message);
              setError(msg);
            } finally {
              setLoading(false);
            }
          }}
        >
          {t?.sb_save || 'Save'}
        </button>
      </Box>
    </Box>
  );
}

export default CreationSettingsPage;
